import type { ReelEditorRow } from './useReelEditor';
import type { ReelSectionWord } from '@src/services/reel/reelSectionWords';
import type { ReelChapter } from '@src/services/reel/reelTypes';

/**
 * PLAT-11074 — what the Reel Editor's search matches on.
 *
 * A reel is a short ordered list, not a scroll of prose, so the useful answer
 * to a query is "these Sections" rather than a walk through highlighted matches
 * (DS3). These two helpers are the whole of the matching rule; the panel filters
 * with them and renders what is left.
 */

/** Nothing to filter by: whitespace is not a query. */
export const normalizeReelQuery = (query: string): string =>
  query.trim().toLowerCase();

/**
 * A Section matches on the name the row shows — the stored one, or the opening
 * words standing in for it — or on anything said inside it (`searchText`, built
 * lower-cased by `deriveReelSections`).
 *
 * `query` is expected normalized; `reelRowMatches` is called once per row per
 * keystroke and has no business re-lowering the same string each time.
 */
export function reelRowMatches(row: ReelEditorRow, query: string): boolean {
  if (!query) return true;
  return (
    row.displayName.toLowerCase().includes(query) ||
    row.searchText.includes(query)
  );
}

/**
 * The heading a chapter actually shows: its title, or the ordinal fallback an
 * unnamed chapter renders (DS5 of PLAT-11055). Search matches what the creator
 * can read, so "chapter 2" finds the second one even though nothing is stored.
 */
export function chapterDisplayTitle(
  chapter: ReelChapter,
  ordinal: number,
): string {
  return chapter.title || `Chapter ${ordinal}`;
}

/**
 * One place the query was found: which Section, and the reel time to put the
 * playhead at to hear it. What Enter walks through, and what the field's
 * "n of m" counts.
 */
export type ReelSearchMatch = {
  sectionId: string;
  time: number;
};

/**
 * Every occurrence of `query` in one Section's words, in reading order.
 *
 * Matched against the words joined back into a line rather than word by word,
 * so a query spanning two of them ("ai algorithm") is one match rather than
 * none — and each is timed at the word it starts in, which is the word the
 * playhead lands on.
 *
 * `query` is expected normalized (see {@link normalizeReelQuery}); the words
 * are lowered here, once per Section per search.
 */
export function sectionSearchMatches(
  sectionId: string,
  words: readonly ReelSectionWord[],
  query: string,
): ReelSearchMatch[] {
  if (!query || words.length === 0) return [];

  // Where each word begins in the joined line, so a match offset can be read
  // back as the word it fell in.
  const wordStarts: number[] = [];
  let line = '';
  for (const word of words) {
    wordStarts.push(line.length);
    line += `${word.value.toLowerCase()} `;
  }

  const matches: ReelSearchMatch[] = [];
  // Occurrences come out in ascending order, so the walk over `wordStarts`
  // carries on from the last one rather than searching from the top each time.
  let wordIndex = 0;
  let from = 0;

  for (;;) {
    const at = line.indexOf(query, from);
    if (at === -1) break;
    while (
      wordIndex + 1 < wordStarts.length &&
      wordStarts[wordIndex + 1] <= at
    ) {
      wordIndex += 1;
    }
    matches.push({ sectionId, time: words[wordIndex].startTime });
    from = at + query.length;
  }

  return matches;
}
